// Everything a matrix-family block draws from, resolved once for whichever flavour is mounting it.
//
// The seven flavours differ in three places only -- the controls strip above the chart, the list
// below it, and which views they offer -- so each has a Block of its own for those. What they share
// is here: the state, the surfaces that are the same for all of them, and the flags deciding what
// is on screen at all.

import { useRef, useMemo } from 'react'
import { omit, isEqual } from 'lodash'

import { isMapOrNone, isWCType as calcIsWCType } from '@aytm/app/stats/page/components/lib/utils'
import { CHART_VIEW_MAPPING } from '@aytm/app/stats/page/question-views/components/lib/constants'
import { resolveAvailableViewModesByFormat } from '@aytm/app/stats/page/question-views/components/controls/lib/view-modes'

import { calcControlsProps } from '@aytm/app/stats/page/question-views/answer-variants/lib/props/controls'
import { calcWordCloudProps } from '@aytm/app/stats/page/question-views/answer-variants/lib/props/word-cloud'
import { calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import {
  buildBatchProps,
  calcBatchComponents,
  calcBatches
} from '@aytm/app/stats/page/question-views/mx/lib/props/batches'
import { calcChartProps } from '@aytm/app/stats/page/question-views/mx/lib/props/chart'
import { getChartTopsMapping } from '@aytm/app/stats/page/question-views/mx/lib/utils'
import useMatrixState from '@aytm/app/stats/page/question-views/mx/states/useMatrixState'

// The views this question offers, which decide both what the strip lists and whether it is worth
// drawing at all. A multi-pick declares one list per form it takes.
export const calcViewModes = (type, subType, format) => {
  const declared = CHART_VIEW_MAPPING[type]?.[subType] || CHART_VIEW_MAPPING[type] || []

  return resolveAvailableViewModesByFormat(declared, format)
}

export default function useMatrixBlock(props) {
  const {
    id,
    questionKey,
    type,
    subType,
    format,
    components,
    answer,
    batches,
    degree,
    maxAnswerPosition,
    globalChartMode,
    handlers = {}
  } = props

  const viewModes = useMemo(() => calcViewModes(type, subType, format), [type, subType, format])

  // only a box score reads tops, and only two flavours ever offer one
  const topsModes = useMemo(
    () => getChartTopsMapping(type, batches || [], maxAnswerPosition),
    [type, batches, maxAnswerPosition]
  )

  const state = useMatrixState({
    params: props.params,
    paramsRevision: props.paramsRevision,
    questionKey,
    type,
    subType,
    format,
    answer,
    batches,
    degree,
    viewModes,
    topsModes,
    defaultSortOrder: props.defaultSortOrder,
    labelTarget: props.labelTarget,
    onReady: props.onReady,
    onStateChange: props.onStateChange,
    onBatchParamsChange: props.onBatchParamsChange,
    onBatchWcParamsChange: props.onBatchWcParamsChange,
    onBatchReady: props.onBatchReady
  })

  const { viewMode, labelsMode, topsMode, chartMode, zoomLevel, sortOrder, uncollapsed } = state

  const items = useMemo(() => calcBatches(batches), [batches])

  const batchComponents = useMemo(() => calcBatchComponents(components, items), [components, items])

  // The props an open batch's block draws from. It is a question of its own as far as the report
  // params go, so it announces its own changes and this block forwards them to the shell.
  const batchPropsOf = useMemo(
    () =>
      buildBatchProps({
        questionId: id,
        questionKey,
        type,
        subType,
        format,
        batchComponents,
        answer,
        degree,
        scaleTmpl: props.scaleTmpl,
        scaleSteps: props.scaleSteps,
        scalePow: props.scalePow,
        defaultSortOrder: props.defaultSortOrder,
        clickable: props.clickable,
        batchParams: props.batchParams,
        paramsRevision: props.paramsRevision,
        handlers: {
          ...handlers,
          onBatchReady: state.onBatchReady,
          onBatchStateChange: state.onBatchStateChange,
          onBatchWcParamsChange: state.onBatchWcParams
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      questionKey,
      type,
      subType,
      format,
      batchComponents,
      answer,
      degree,
      handlers,
      state.onBatchReady,
      state.onBatchStateChange,
      state.onBatchWcParams,
      props.scaleTmpl,
      props.scaleSteps,
      props.scalePow,
      props.defaultSortOrder,
      props.clickable,
      props.batchParams,
      props.paramsRevision
    ]
  )

  const question = { ...props, viewMode, labelsMode, topsMode, chartMode, zoomLevel, sortOrder }

  // the summary chart draws no image, so a sub-question's image change redraws the item headers alone
  const chartBatchesRef = useRef(null)
  const chartBatches = useMemo(() => {
    const next = batches?.map(batch => omit(batch, 'image'))
    if (isEqual(next, chartBatchesRef.current)) return chartBatchesRef.current
    chartBatchesRef.current = next
    return next
  }, [batches])

  const chartHandlers = useMemo(
    () => ({
      ...handlers,
      onChartModeChange: state.onChartModeChange,
      onZoomChange: state.onZoomChange,
      // the chart hands its api to both sides: the shell serializes the svg, the state reads the axis
      onChartReady: api => {
        state.onChartReady(api)
        handlers?.onChartReady?.(api)
      }
    }),
    [handlers, state.onChartModeChange, state.onZoomChange, state.onChartReady]
  )

  // a clicked column names the order it wants; the state is what settles on one
  const onTableSort = useMemo(
    () => (colIndex, dir, cols) => {
      const order = calcSortOrder(cols, colIndex, dir)
      if (order) state.onSortChange(order)
    },
    [state.onSortChange]
  )

  return {
    ...state,
    viewModes,
    topsModes,
    // A question with one sub-question is read through that sub-question alone -- there is nothing
    // to summarise across it, so only the batch below is drawn.
    summaryOn: true === components?.summary || 1 < items.length,
    // a report drawn as a map or as nothing at all has no chart to offer, and no view to pick for one
    chartOn: !isMapOrNone(chartMode, globalChartMode),
    isWCType: calcIsWCType({ type, variant: props.variant, questionType: props.questionType, batches }),
    itemsControlsProps: calcControlsProps({
      ...question,
      handlers: { ...handlers, onChartModeChange: state.onWcToggle }
    }),
    cloudProps: calcWordCloudProps({
      ...question,
      handlers: { ...handlers, onWcReady: state.onWcReady },
      onWcParamsChange: props.onWcParamsChange
    }),
    chartProps: calcChartProps({ ...question, batches: chartBatches, handlers: chartHandlers }),
    batchesProps: {
      questionId: id,
      batches: items,
      questionType: type,
      components: batchComponents,
      uncollapsed,
      batchPropsOf
    },
    // what every list below the chart is built from, whichever of them this flavour draws
    tableInputs: { ...question, handlers: { ...handlers, onSort: onTableSort } }
  }
}
